(() => {
  if (customElements.get('waxer-shipping-summary')) return;
  const flatCountries = new Set('AT BG HR CZ DK EE FI FR HU IE IT LV LT LU MC PL PT SK SI ES SE'.split(' '));
  class WaxerShippingSummary extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        document.addEventListener('waxer:delivery-country', this.syncCountry);
        document.addEventListener('waxer:cart-changed', this.cartChanged);
        this.refreshCart();
        return;
      }
      this.initialized = true;
      if (this.dataset.itemQuantity !== undefined) {
        document.querySelectorAll('[data-waxer-cart-quantity]').forEach(label => {
          label.textContent = `(${this.dataset.itemQuantity} ${window.waxerT('items')})`;
        });
      }
      // MiniCart's ancestor change handler is only meant for quantity inputs.
      this.addEventListener('change', event => event.stopPropagation());
      this.addEventListener('click', event => event.stopPropagation());
      this.select = this.querySelector('[data-country-select]');
      this.shippingValue = this.querySelector('[data-shipping-value]');
      this.totalValue = this.querySelector('[data-total-value]');
      this.message = this.querySelector('[data-cost-message]');
      this.details = this.querySelector('[data-exact-details]');
      this.exactMessage = this.querySelector('[data-exact-message]');
      this.button = this.querySelector('[data-calculate]');
      this.postcode = this.querySelector('[data-postcode]');
      this.nl = this.dataset.language === 'nl';
      this.subtotal = Number(this.dataset.subtotal);
      this.currency = this.dataset.currency || 'EUR';
      this.sequence = 0;
      try {
        const remembered = sessionStorage.getItem('waxer-delivery-country');
        if (Array.from(this.select.options).some(o => o.value === remembered)) this.select.value = remembered;
      } catch (_) {}
      this.select.addEventListener('change', () => {
        try { sessionStorage.setItem('waxer-delivery-country', this.select.value); } catch (_) {}
        this.sequence++;
        this.postcode.value = '';
        this.exactMessage.textContent = '';
        this.button.disabled = false;
        this.estimate();
        document.dispatchEvent(new CustomEvent('waxer:delivery-country', {detail:this.select.value}));
      });
      this.syncCountry = event => {
        if (this.select.value === event.detail || !Array.from(this.select.options).some(o => o.value === event.detail)) return;
        this.select.value = event.detail;
        this.sequence++;
        this.postcode.value = '';
        this.exactMessage.textContent = '';
        this.button.disabled = false;
        this.estimate();
      };
      document.addEventListener('waxer:delivery-country', this.syncCountry);
      this.button.addEventListener('click', () => this.calculate());
      this.postcode.addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); this.calculate(); }
      });
      this.estimate();
      this.cartChanged = () => this.refreshCart();
      document.addEventListener('waxer:cart-changed', this.cartChanged);
      this.refreshCart();
    }
    disconnectedCallback() { document.removeEventListener('waxer:delivery-country', this.syncCountry); document.removeEventListener('waxer:cart-changed', this.cartChanged); this.sequence++; this.cartRequest = (this.cartRequest || 0) + 1; }
    async refreshCart() {
      const request = this.cartRequest = (this.cartRequest || 0) + 1;
      try {
        const response = await fetch(`${this.dataset.root || '/'}cart.js`, {cache:'no-store',headers:{Accept:'application/json'}});
        if (!response.ok) return;
        const cart = await response.json();
        if (!this.isConnected || request !== this.cartRequest) return;
        this.subtotal = Number(cart.total_price);
        this.dataset.weight = cart.total_weight;
        this.dataset.itemQuantity = cart.item_count;
        this.dataset.shipping = String(cart.requires_shipping);
        this.currency = cart.currency || this.currency;
        const original = this.querySelector('[data-original-subtotal]');
        if (original) original.textContent = this.money(cart.original_total_price);
        const savings = Math.max(0, Number(cart.original_total_price) - Number(cart.total_price));
        let row = this.querySelector('[data-discount-row]');
        // A section response can omit a previously hidden row. Recreate it from
        // the same confirmed cart response used for the subtotal and total.
        if (!row && original) {
          row = document.createElement('div');
          row.className = 'waxer-costs-row waxer-discount-row';
          row.setAttribute('data-discount-row', '');
          const label = document.createElement('span');
          label.setAttribute('data-discount-label', '');
          const value = document.createElement('span');
          value.setAttribute('data-discount-value', '');
          row.append(label, value);
          original.parentElement.after(row);
        }
        if (row) {
          row.hidden = !(savings > 0);
          const titles = (cart.cart_level_discount_applications || []).map(d => d.title).filter(Boolean);
          this.querySelector('[data-discount-label]').textContent = `${window.waxerT('discount')}${titles.length ? ' · ' + titles.join(', ') : ''}`;
          this.querySelector('[data-discount-value]').textContent = `−${this.money(savings)}`;
        }
        document.querySelectorAll('.waxer-welcome-progress').forEach(notice => {
          notice.hidden = savings > 0;
          const remaining = Math.max(0, 2500 - cart.original_total_price);
          const text = notice.querySelector('span');
          if (text && remaining > 0) text.textContent = window.waxerT('welcome_remaining', {amount:this.money(remaining)});
          else if (text && savings === 0) text.textContent = window.waxerT('welcome_check');
        });
        this.estimate();
      } catch (_) { /* Keep confirmed values on a temporary network error. */ }
    }
    money(cents) {
      return new Intl.NumberFormat(this.dataset.language || 'en', {style:'currency',currency:this.currency}).format(cents / 100);
    }
    display(cents) {
      this.shippingValue.textContent = cents === 0 ? (window.waxerT('free')) : this.money(cents);
      this.totalValue.textContent = this.money(this.subtotal + cents);
    }
    estimate() {
      const country = this.select.value;
      this.querySelectorAll('[data-destination-name]').forEach(label => {
        label.textContent = this.select.selectedOptions[0].textContent.trim();
      });
      let cents = null;
      const freeThreshold = ['NL','BE','DE'].includes(country) ? 5000 : 15000;
      this.message.textContent = '';
      if (this.dataset.shipping === 'false') cents = 0;
      else if (this.currency === 'EUR' && Number(this.dataset.weight) <= 40000) {
        if (country === 'NL') cents = Number(this.dataset.weight) <= 360 ? 395 : 895;
        else if (['BE','DE'].includes(country)) cents = 895;
        else if (flatCountries.has(country)) cents = 1495;
        else if (country === 'RO') cents = 2995;
        if (cents !== null && this.subtotal >= freeThreshold) cents = 0;
      }
      if (cents !== null) {
        this.display(cents);
        this.details.hidden = true;
        this.details.open = false;
        if (cents > 0 && this.currency === 'EUR') {
          this.message.textContent = window.waxerT('shipping_remaining', {threshold:this.money(freeThreshold),amount:this.money(freeThreshold-this.subtotal)});
        } else if (this.dataset.shipping !== 'false') this.message.textContent = window.waxerT('qualified');
      } else {
        this.details.hidden = false;
        this.shippingValue.textContent = window.waxerT('calculate_below');
        this.totalValue.textContent = this.money(this.subtotal) + (window.waxerT('plus_shipping'));
        this.message.textContent = window.waxerT('postal_destination');
        this.details.open = true;
      }
    }
    async calculate() {
      const zip = this.postcode.value.trim();
      if (!zip) {
        this.exactMessage.textContent = window.waxerT('enter_postcode');
        this.postcode.focus(); return;
      }
      const sequence = ++this.sequence;
      this.button.disabled = true;
      this.exactMessage.textContent = window.waxerT('loading_rates');
      const root = this.dataset.root || '/';
      const params = new URLSearchParams({'shipping_address[country]':this.select.selectedOptions[0].dataset.name,'shipping_address[zip]':zip});
      try {
        const prep = await fetch(`${root}cart/prepare_shipping_rates.json?${params}`, {method:'POST',headers:{Accept:'application/json'}});
        if (!prep.ok) throw new Error('rate_request');
        let result = null;
        for (let attempt=0; attempt<8; attempt++) {
          if (sequence !== this.sequence || !this.isConnected) return;
          if (attempt) await new Promise(resolve => setTimeout(resolve, 750));
          const response = await fetch(`${root}cart/async_shipping_rates.json?${params}`, {headers:{Accept:'application/json'}});
          if (!response.ok) throw new Error('rate_response');
          result = await response.json();
          if (result !== null) break;
        }
        if (sequence !== this.sequence || !this.isConnected) return;
        const rates = result?.shipping_rates?.filter(rate => Number.isFinite(Number(rate.price)) && (!rate.currency || rate.currency === this.currency));
        if (!rates?.length) throw new Error('no_rates');
        const rate = rates.reduce((best,next) => Number(next.price) < Number(best.price) ? next : best);
        this.display(Math.round(Number(rate.price)*100));
        this.message.textContent = rate.presentment_name || rate.name || '';
        this.exactMessage.textContent = window.waxerT('calculated');
      } catch (_) {
        if (sequence !== this.sequence || !this.isConnected) return;
        this.exactMessage.textContent = window.waxerT('no_rate');
      } finally {
        if (sequence === this.sequence && this.isConnected) this.button.disabled = false;
      }
    }
  }
  customElements.define('waxer-shipping-summary', WaxerShippingSummary);
})();
